import {FACTIONS} from '../../constants/factions';
import {UNIT_TYPES} from '../units';
import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {CENTRAL_QUARTER_PREFIX, COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from '../../constants/tokens';
import {CHESTS} from '../../constants/chests';
import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {buildTutorialBarbaSteps} from './tutorialBarbaReplicas';

// Токен центрального квартала несет цвет владельца; игрок в обучении — синий
const PLAYER_CENTRAL_QUARTER_TOKEN = `${CENTRAL_QUARTER_PREFIX}blue`;

/**
 * Глава 2: фракции, спецстроения (склад/арсенал/дворец/лагерь), нехватка ферм
 * и цепочки снабжения, перехват инициативы, основание нового города и зона
 * влияния кварталов, стойкость орков в бою и захват города противника.
 *
 * Стартует со снапшота public/fields/tutorial_chapter2.json (см. генератор
 * developer_artifacts/scripts/generateTutorialSnapshots.cjs): раунд 4, у
 * игрока два города (итог главы 1) и четыре отряда, у орков город на юге,
 * рейдер d10(10) на (3,3) и гарнизон d6(6) на центральном квартале (0,-4).
 *
 * Сценарий по раундам:
 *  Р4: снабжения на всех не хватает (1 ферма) -> строим вторую ферму и склад
 *      рядом с ней (та же ферма начинает давать 2 единицы);
 *      бот перехватывает инициативу, его рейдер первым ходом бьет дозор на
 *      руднике (1,4) и блокирует рудник. Кавалерия d12 (3) голодает до 1.
 *  Р5: снабжение всех отрядов (склад дает ферме 1,1 две единицы); арсенал +
 *      свой перехват инициативы; две докупки d10 (6 -> 10, максимум) через
 *      арсенал; марш
 *      d10 к рейдеру; бой — рейдер уничтожен (стойкость орков).
 *  Р6: дворец и казарма у южного города (0,-1); d12 (вне казармы росла на
 *      +1 за снабжение: 1 -> 3) встает на эту казарму.
 *  Р7: d12 на казарме растет как все (+2 -> 5); лагерь рядом с казармой,
 *      квартал и ферма под отрядом d10 — вторая вплотную к складу, обе дают
 *      по 2 (3 действия благодаря дворцу); найм новобранцев d6 в северной
 *      казарме; три докупки d12 (-> 8).
 *  Р8: снабжение показывает разницу: d12 у лагеря +4 (8 -> 12, максимум),
 *      новобранцы в обычной казарме +2 (1 -> 3); новый город на западе
 *      (3 действия): дорога -3,2 от Великого рудника, Центральный квартал
 *      -4,2 за зоной кварталов и Базовый квартал -5,3 у Великих руин;
 *      первый шаг d12 в степь (0,-2) — на пустой ячейке перемещение
 *      останавливается.
 *  Р9: снабжение до d12 на пустой ячейке еще дотягивается (путь по казарме);
 *      второй шаг (0,-3) — вплотную к центру орков (0,-4); бой — гарнизон
 *      уничтожен.
 *  Р10: между казармой и d12 пустая ячейка — снабжение не проходит (-2);
 *      d12 занимает БАЗОВЫЙ квартал (захват работает через любой квартал) —
 *      город орков захвачен.
 *
 * Формат шагов/ботскрипта — см. chapter1.js.
 */
export const TUTORIAL_CHAPTER_2 = {
  id: 'tutorial_chapter_2',
  title: 'Обучение: Глава 2',
  shortTitle: 'Глава 2',
  description: 'Фракции, специальные строения, цепочки снабжения, перехват инициативы и новые города',
  fieldPath: '/fields/tutorial_chapter2.json',
  groundBackground: GROUND_BACKGROUNDS.GREEN_2,
  playerFaction: FACTIONS.HUMANS,
  botFaction: FACTIONS.ORCS,
  botName: 'Вождь Орков',
  // Кадр главы остается по центру поля (см. applyTutorialCameraShift): задания
  // тут идут по ВСЕЙ высоте поля — от своего города на юге до захвата города
  // орков на севере (1,-4), и обычный сдвиг вниз уводил бы финал главы за
  // верхний край
  cameraDownShiftFraction: 0,
  // Сценарий главы: раунды 4-7 (см. док-комментарий выше)
  finalRound: 13,
  // Награда за ПЕРВОЕ прохождение главы (начисляет сервер)
  reward: {type: 'chest', chestId: CHESTS.TUTORIAL_SKINS_GOLD},
  ui: {
    hidePolicyMarket: true,
    hideInitiativeInterception: false,
    // Кнопка отмены знакома с главы 1 — видна сразу (отмена в обучении
    // все равно отключена, кнопка отвечает подсказкой)
    undoButtonRevealed: true,
  },

  steps: [
    // Реплики Барбы — цепочка окон до приветствия главы
    ...buildTutorialBarbaSteps('ch2'),
    {
      id: 'welcome',
      modalId: 'ch2_welcome',
    },
    {
      // Знакомство с полными правилами: пункт «Правила» меню матча;
      // задание — открыть раздел «Фракции». Пока правила открыты,
      // следующая обучающая модалка ждет (TutorialModalHost)
      id: 'open_rules_factions',
      modalId: 'ch2_rules_hint',
      // На широких экранах (>=1344px) панель разделов правил закреплена и
      // «открыта» всегда — первый кандидат (ссылка раздела) виден сразу,
      // кандидат кнопки-бургера отсеивается ее скрытием (display: none)
      task: {
        text: 'Открой Правила в меню матча (кнопка меню справа в шапке) и перейди в раздел «Фракции»',
        // Цепочка (первый ВИДИМЫЙ побеждает): ссылка раздела «Фракции» в
        // открытой панели разделов -> кнопка открытия панели разделов внутри
        // правил -> пункт «Правила» в открытом меню матча -> кнопка меню в
        // шапке
        // Ссылка раздела — только при ОТКРЫТОЙ панели разделов (--open):
        // задвинутая панель клипается overflow'ом модалки, но остается в
        // границах вьюпорта, и голый селектор ловил бы невидимую ссылку
        arrowUiSelectorCandidates: [
          '.rules-modal_sections-panel--open [data-tutorial-select="rules_section_factions"]',
          '[data-tutorial-select="rules_sections_toggle"]',
          '[data-tutorial-select="in_game_menu_rules"]',
          '.app-header_menu-button',
        ],
        // Цели внутри открытых правил — стрелка поверх их оверлея (z 1500)
        arrowAboveModals: true,
      },
      allow: [],
      // 'factions' = RULES_SECTION_IDS.FACTIONS (rulesSections.js импортирует
      // React-компоненты — конфиг главы грузится и сервером, поэтому литерал)
      complete: {check: 'rulesSectionOpened', sectionId: 'factions'},
    },
    {
      // Правила открыты — просим пролистать страницу: главное про фракции
      // как раз ниже первого экрана. Ни modalId, ни стрелки у шага нет и быть
      // не может: под оверлеем правил обучающая модалка не показывается
      // (TutorialModalHost), а стрелку на открытых правилах уводит на кнопку
      // «Скрыть». Подсказку рисует TutorialRulesScrollHint внутри правил, она
      // же засчитывает прокрутку
      id: 'scroll_rules_factions',
      task: {
        text: 'Пролистай страницу раздела «Фракции» вниз — там описана каждая фракция',
        hint: 'Если правила закрыты, открой их в меню матча и вернись в раздел «Фракции»',
      },
      allow: [],
      complete: {check: 'rulesSectionScrolled', sectionId: 'factions'},
    },
    {
      // Два способа открыть карточку фракции: из панели игроков и с кнопки
      // завершения хода (иконка фракции). Первый шаг — из панели
      id: 'faction_card_from_panel',
      modalId: 'ch2_faction_cards',
      task: {
        text: 'Открой список игроков (кружки в шапке), нажми на игрока и закрой его карточку фракции',
        // Карточка открыта -> стрелка на ее кнопку «Скрыть» (оверлей карточки
        // z-index 9999 — потому arrowAboveModals)
        arrowUiSelectorCandidates: [
          '[data-tutorial-select="faction_card_close"]',
          '.player-list .player-info_faction-icon',
          // Список закрыт — стрелка на строку игроков в шапке (на телефоне —
          // полоса под шапкой), которая его открывает
          '.header-players-row',
        ],
        arrowAboveModals: true,
      },
      allow: [],
      complete: {check: 'factionCardViewedFrom', sources: ['players_panel']},
    },
    {
      id: 'supply_shortage',
      modalId: 'ch2_supply_shortage',
      task: {
        text: 'Ферма одна — снабди отряд Тяжелой пехоты на Казарме, остальные потеряют по 2 размера',
        arrowHexKey: '-1,3',
      },
      allow: [{action: 'supply', farmHexKey: '-1,3', unitHexKey: '0,2'}],
      complete: {check: 'unitSuppliedAt', hexKey: '0,2'},
    },
    {
      id: 'complete_supply_r4',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, currentPlayerColor: 'blue'},
    },
    {
      // Сначала ферма, потом склад рядом: игрок видит, как ТА ЖЕ ферма
      // начинает давать две единицы снабжения
      id: 'build_farm2',
      task: {
        text: 'Построй вторую Ферму на указанной ячейке — у квартала под будущий склад',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.FARM, hexKey: '1,1'}],
      complete: {check: 'hexToken', hexKey: '1,1', token: COMMON_BUILDING_TOKENS.FARM},
    },
    {
      id: 'build_warehouse',
      modalId: 'ch2_warehouse',
      task: {
        text: 'Построй Склад на квартале вплотную к новой ферме — она станет давать 2 единицы снабжения',
        hint: 'Выбери Склад в панели снизу и нажми на указанную ячейку',
        arrowHexKey: '1,2',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.WAREHOUSE, hexKey: '1,2'}],
      complete: {check: 'specialBuildingAt', hexKey: '1,2', token: SPECIAL_BUILDING_TOKENS.WAREHOUSE},
    },
    {
      id: 'complete_construction_r4',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      // Шаг закрывается перехватом инициативы ботом в его фазу Строительства
      complete: {check: 'initiativeInterceptedBy', color: 'red'},
    },
    {
      // Игрок должен УВИДЕТЬ перехват: модалка через 3 с после него, а рейдер
      // бота не двинется, пока игрок ее не закроет (см. waitTutorialStepPassed
      // в botScript '4|war|movement')
      id: 'enemy_intercepted',
      modalId: 'ch2_enemy_intercepted',
      modalDelayMs: 3000,
    },
    {
      // Второй способ открыть карточку фракции — в ХОД ПРОТИВНИКА: на кнопке
      // завершения хода его фракция (в свой ход клик по иконке отключен —
      // кнопка с галочкой завершает фазу). Рейдер бота ждет и этот шаг
      // (waitTutorialStepPassed в botScript)
      id: 'faction_card_from_button',
      task: {
        text: 'Пока Орки ходят — нажми на иконку их фракции на кнопке завершения хода и ознакомься с их особенностями',
        arrowUiSelectorCandidates: [
          '[data-tutorial-select="faction_card_close"]',
          '.complete-phase-button .player-faction-icon-container',
        ],
        arrowAboveModals: true,
        duringOpponentTurn: true,
      },
      allow: [],
      complete: {check: 'factionCardViewedFrom', sources: ['complete_button']},
    },
    {
      id: 'watch_enemy_attack',
      // Кости боя рейдера с дозором: дозор (защитник) бросает первым.
      // Дозор 2: -3 и -2 -> урон 0. Рейдер d10 8 (голодал раунд): 0 и 0 ->
      // урон 8 — дозор уничтожен, рейдер встает вплотную к руднику (1,4)
      dice: {damage: [-3, -2, 0, 0]},
      // Этап ближнего боя бота завершает живой игрок кнопкой завершения хода
      // (механика локальной партии) — задача это подсказывает
      task: {
        text: 'Рейдер Орков атакует твой отряд — подтверди итоги боя',
        arrowUiSelector: '.complete-phase-button',
        // Задание выполняется в ход бота — панель не подменяет текст на
        // «Дождись окончания хода противника»
        duringOpponentTurn: true,
      },
      allow: [{action: 'completeTurn'}, {action: 'meleeInteraction'}],
      complete: {check: 'gameMoment', phase: PHASES.WAR, currentPlayerColor: 'blue'},
    },
    {
      id: 'mine_blocked',
      modalId: 'ch2_mine_blocked',
      task: {
        text: 'Сейчас ты лишком слаб, чтобы отомстить — заверши этапы своего хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 5, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_all_r5',
      modalId: 'ch2_farm_chain',
      task: {
        text: 'Снабди все три отряда: теперь снабжения хватает',
        hint: 'Ферма у склада дает 2 единицы, дальняя ферма — 1',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '2,0'},
        ],
      },
    },
    {
      id: 'complete_supply_r5',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 5, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_arsenal',
      modalId: 'ch2_arsenal',
      // Арсенал строится на квартале 0,4: он не смежен рейдеру на 2,3 —
      // строение рядом с вражеским отрядом не работало бы
      task: {
        text: 'Построй Арсенал на свободном квартале',
        arrowHexKey: '0,4',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.ARSENAL, hexKey: '0,4'}],
      complete: {check: 'specialBuildingAt', hexKey: '0,4', token: SPECIAL_BUILDING_TOKENS.ARSENAL},
    },
    {
      id: 'intercept_initiative',
      modalId: 'ch2_intercept',
      task: {
        text: 'Перехвати инициативу кнопкой со стрелками в нижней панели',
        hint: 'Перехват стоит одно действие фазы Строительства',
        arrowUiSelector: '[data-tutorial-select="initiative_interception"]',
      },
      allow: [{action: 'interceptInitiative'}],
      complete: {check: 'initiativeInterceptedBy', color: 'blue'},
    },
    {
      id: 'complete_construction_r5',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 5,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'size_d10_arsenal',
      task: {
        text: 'Докупи Тяжелой пехоте размер до максимума (10): две докупки — благодаря арсеналу каждая даёт +2',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'sizeRecruitment'}],
      complete: {check: 'unitSizeRecruitedAt', hexKey: '0,2', count: 2},
    },
    {
      id: 'complete_recruitment_r5',
      task: {
        text: 'Заверши этап найма',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.MOVEMENT,
        minRound: 5,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'move_d10',
      modalId: 'ch2_revenge_plan',
      task: {
        text: 'Подведи Тяжёлую пехоту на указанную ячейку — вплотную к рейдеру Орков',
        arrowHexKey: '2,2',
      },
      allow: [{action: 'moveUnit', fromHexKey: '0,2', toHexKey: '2,2'}],
      complete: {check: 'unitMovedTo', toHexKey: '2,2', color: 'blue'},
    },
    {
      id: 'enter_combat_r5',
      // Порядок потребления костей = порядок отрядов в состоянии: отряды
      // игрока в снапшоте перечислены первыми. d10 10: 0 и 0 -> урон 10 ->
      // орки теряют 2 за каждые 3 урона (-6) — рейдер d10 (6 после двух
      // голодных раундов) уничтожен. Рейдер: -3 и -2 -> урон 1
      dice: {damage: [0, 0, -3, -2]},
      task: {
        text: 'Заверши этап перемещения — начнётся ближний бой с рейдером',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.COMBAT,
        minRound: 5,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'resolve_combat_r5',
      modalId: 'ch2_orc_resilience',
      task: {
        text: 'Изучи результат боя и заверши этапы до конца хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}, {action: 'meleeInteraction'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 6, currentPlayerColor: 'blue'},
    },

    // === Раунд 6: дворец, казарма у южного города, кавалерия на казарму ===
    {
      id: 'supply_r6',
      task: {
        text: 'Снабди все три отряда',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '2,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '2,0'},
        ],
      },
    },
    {
      id: 'complete_supply_r6',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 6, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_palace',
      modalId: 'ch2_palace',
      task: {
        text: 'Построй Дворец на квартале южного города',
        arrowHexKey: '-1,1',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.PALACE, hexKey: '-1,1'}],
      complete: {check: 'specialBuildingAt', hexKey: '-1,1', token: SPECIAL_BUILDING_TOKENS.PALACE},
    },
    {
      id: 'build_barracks_south',
      task: {
        text: 'Построй Казарму у южного города — на ней будет расти кавалерия',
        arrowHexKey: '0,-1',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.BARRACKS, hexKey: '0,-1'}],
      complete: {check: 'hexToken', hexKey: '0,-1', token: COMMON_BUILDING_TOKENS.BARRACKS},
    },
    {
      id: 'complete_construction_r6',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        minRound: 6,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'move_d12_to_barracks',
      modalId: 'ch2_cavalry_to_barracks',
      // Путь по ферме 1,1, дороге 0,1 и центру южного города: перемещение
      // не идет сквозь пустые ячейки, а через свой отряд на дороге — проходит
      task: {
        text: 'Заверши найм и поставь Тяжёлую кавалерию на новую казарму',
        arrowHexKey: '0,-1',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '2,0', toHexKey: '0,-1'}],
      complete: {check: 'unitMovedTo', toHexKey: '0,-1', color: 'blue'},
    },
    {
      id: 'complete_war_r6',
      task: {
        text: 'Заверши оставшиеся этапы хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 7, currentPlayerColor: 'blue'},
    },

    // === Раунд 7: казарма дает +2, лагерь и квартал, докупки, возврат d10 ===
    {
      id: 'supply_r7',
      modalId: 'ch2_barracks_growth',
      task: {
        text: 'Снабди все три отряда — кавалерия на казарме вырастет на 2',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '2,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '0,-1'},
        ],
      },
    },
    {
      id: 'complete_supply_r7',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 7, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_camp',
      modalId: 'ch2_camp',
      task: {
        text: 'Построй Лагерь на квартале рядом с новой казармой',
        arrowHexKey: '-1,0',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.MILITARY_CAMP, hexKey: '-1,0'}],
      complete: {check: 'specialBuildingAt', hexKey: '-1,0', token: SPECIAL_BUILDING_TOKENS.MILITARY_CAMP},
    },
    {
      id: 'build_quarter_south',
      task: {
        text: 'Построй ещё один Базовый квартал южного города — каждый квартал даёт 1 докупку размера за раунд',
        arrowHexKey: '1,-1',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '1,-1'}],
      complete: {check: 'hexToken', hexKey: '1,-1', token: COMMON_BUILDING_TOKENS.QUARTER},
    },
    {
      // Третье действие дворца: ферма прямо под отрядом d10 (2,2) — вторая
      // ферма вплотную к складу, обе дают по 2 единицы снабжения (строить на
      // ячейке со СВОИМ отрядом можно — блокируют только вражеские)
      id: 'build_farm_at_warehouse',
      task: {
        text: 'Третьим действием построй Ферму под Тяжелой пехотой — вплотную к складу',
        arrowHexKey: '2,2',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.FARM, hexKey: '2,2'}],
      complete: {check: 'hexToken', hexKey: '2,2', token: COMMON_BUILDING_TOKENS.FARM},
    },
    {
      id: 'complete_construction_r7',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 7,
        currentPlayerColor: 'blue',
      },
    },
    {
      // Новобранцы в обычной казарме — контрольный отряд для сравнения с
      // ростом у лагеря в следующую фазу Снабжения
      id: 'recruit_d6_north',
      task: {
        text: 'Найми обычную Пехоту в казарме северного города — с ней сравним рост у лагеря',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'recruitUnit', unitType: UNIT_TYPES.D6.id, hexKey: '0,2'}],
      complete: {check: 'unitRecruitedAt', hexKey: '0,2', type: UNIT_TYPES.D6.id},
    },
    {
      id: 'size_d12_r7',
      task: {
        text: 'Докупи кавалерии размер трижды: нажми на неё, затем на подсвеченные кварталы южного города',
        arrowHexKey: '0,-1',
      },
      // Нанятые новобранцы автовыбраны — сбрасываем, игрок сам выбирает d12
      deselectsUnitOnEntry: true,
      allow: [{action: 'sizeRecruitment', unitHexKey: '0,-1'}],
      complete: {check: 'unitSizeRecruitedAt', hexKey: '0,-1', count: 3},
    },
    {
      id: 'complete_war_r7',
      task: {
        text: 'Заверши оставшиеся этапы хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 8, currentPlayerColor: 'blue'},
    },

    // === Раунд 8: +4 у лагеря против +2, максимум d12, первый шаг в степь ===
    {
      id: 'supply_r8',
      task: {
        text: 'Снабди все четыре отряда и заверши фазу — сравни, как вырастут отряды на казармах',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,-1'},
          {check: 'unitSuppliedAt', hexKey: '0,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '2,2'},
        ],
      },
    },
    {
      id: 'complete_supply_r8',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 8, currentPlayerColor: 'blue'},
    },
    {
      // Рост уже применен в конце фазы Снабжения — модалка сравнивает
      // результат: кавалерия у лагеря 8 -> 12, новобранцы в обычной казарме
      // 1 -> 3
      id: 'camp_growth_shown',
      modalId: 'ch2_camp_growth',
    },
    {
      // Новый город на западе: Центральный квартал на -4,2 — первая ячейка за
      // зоной кварталов города с центром 0,0 (его кварталы -1,0 и -1,1 в 3
      // ячейках). Путь строительства — квартал -1,1, Великий рудник -2,1 и
      // дорога этого шага: все пустые соседи рудника (-3,1, -3,2, -2,0, -2,2)
      // еще в зоне
      id: 'build_road_west',
      modalId: 'ch2_new_city',
      task: {
        text: 'Проложи Дорогу от Великого рудника на запад — к месту под новый город',
        arrowHexKey: '-3,2',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.ROAD, hexKey: '-3,2'}],
      complete: {check: 'hexToken', hexKey: '-3,2', token: COMMON_BUILDING_TOKENS.ROAD},
    },
    {
      id: 'build_central_quarter',
      modalId: 'ch2_quarter_zone',
      task: {
        text: 'Основай новый город: построй Центральный квартал на указанной ячейке — она за границей штриховки',
        arrowHexKey: '-4,2',
      },
      allow: [{action: 'build', token: PLAYER_CENTRAL_QUARTER_TOKEN, hexKey: '-4,2'}],
      complete: {check: 'hexToken', hexKey: '-4,2', token: PLAYER_CENTRAL_QUARTER_TOKEN},
    },
    {
      // Базовый квартал у Великих руин (-6,3): руины присоединятся к новому
      // городу. Соседи центра в сторону города 0,0 (-3,1, -3,2) остаются в
      // плотной штриховке — расширяться туда нельзя
      id: 'build_quarter_new_city',
      modalId: 'ch2_new_city_quarter',
      task: {
        text: 'Третьим действием построй Базовый квартал нового города — у Великих руин',
        arrowHexKey: '-5,3',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '-5,3'}],
      complete: {check: 'hexToken', hexKey: '-5,3', token: COMMON_BUILDING_TOKENS.QUARTER},
    },
    {
      id: 'complete_construction_r8',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      // Без этапа: после стройки монет почти нет, и этап найма может
      // пропуститься сам — тогда фаза Войны начнется сразу с перемещения
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        minRound: 8,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'march_into_steppe',
      modalId: 'ch2_steppe_step',
      task: {
        text: 'Заверши найм и выведи кавалерию в степь — на указанную пустую ячейку',
        arrowHexKey: '0,-2',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '0,-1', toHexKey: '0,-2'}],
      complete: {check: 'unitMovedTo', toHexKey: '0,-2', color: 'blue'},
    },
    {
      id: 'complete_war_r8',
      task: {
        text: 'Заверши оставшиеся этапы хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 9, currentPlayerColor: 'blue'},
    },

    // === Раунд 9: снабжение на пустую ячейку, второй шаг, бой с гарнизоном ===
    {
      id: 'supply_r9',
      modalId: 'ch2_supply_to_empty',
      task: {
        text: 'Снабди все четыре отряда — до кавалерии в степи снабжение ещё дотягивается',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,-2'},
          {check: 'unitSuppliedAt', hexKey: '0,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '2,2'},
        ],
      },
    },
    {
      id: 'complete_supply_r9',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 9, currentPlayerColor: 'blue'},
    },
    {
      id: 'skip_construction_r9',
      task: {
        text: 'Строить ничего не нужно — заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        minRound: 9,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'move_d12_to_city',
      task: {
        text: 'Заверши найм и выведи кавалерию ещё на ячейку — вплотную к центру города Орков',
        arrowHexKey: '0,-3',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '0,-2', toHexKey: '0,-3'}],
      complete: {check: 'unitMovedTo', toHexKey: '0,-3', color: 'blue'},
    },
    {
      id: 'enter_combat_r9',
      // Кости в порядке отрядов состояния (d12 игрока раньше гарнизона).
      // d12 12: 0 и 0 -> урон 12: орки теряют 2 за каждые 3 урона -> -8 —
      // гарнизон из 6 уничтожен. Гарнизон: 0 и 0 -> урон 6 — показательно
      // сильный ответный удар: кавалерия 12 -> 6 (запас размера не зря)
      dice: {damage: [0, 0, 0, 0]},
      task: {
        text: 'Заверши этап перемещения — начнётся ближний бой с гарнизоном',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.COMBAT,
        minRound: 9,
        currentPlayerColor: 'blue',
      },
    },
    {
      // Модалка — через секунду после начала этапа ближнего боя (дефолтная
      // задержка), а не сразу после перемещения
      id: 'resolve_combat_r9',
      modalId: 'ch2_garrison_broken',
      task: {
        text: 'Заверши этапы хода — гарнизон падёт в ближнем бою',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}, {action: 'meleeInteraction'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 10, currentPlayerColor: 'blue'},
    },

    // === Раунд 10: рейд без снабжения, захват города ===
    {
      id: 'supply_r10',
      modalId: 'ch2_deep_raid',
      task: {
        text: 'Снабди остальные отряды — до кавалерии снабжение уже не дотянется',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,2'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '2,2'},
        ],
      },
    },
    {
      id: 'complete_supply_r10',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 10, currentPlayerColor: 'blue'},
    },
    {
      id: 'skip_construction_r10',
      task: {
        text: 'Строить ничего не нужно — заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        minRound: 10,
        currentPlayerColor: 'blue',
      },
    },
    {
      // Встаем на БАЗОВЫЙ квартал (1,-4), а не на центральный: игроку видно,
      // что захват работает через любой квартал города (checkCityCapture
      // смотрит центр и все базовые кварталы)
      id: 'capture_orc_city',
      modalId: 'ch2_capture_city',
      task: {
        text: 'Заверши найм и поставь Тяжёлую кавалерию на Базовый квартал города Орков',
        arrowHexKey: '1,-4',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '0,-3', toHexKey: '1,-4'}],
      complete: {check: 'unitMovedTo', toHexKey: '1,-4', color: 'blue'},
    },
    {
      id: 'finish_chapter',
      task: {
        text: 'Заверши оставшиеся этапы — в конце раунда город Орков станет твоим',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'cityOwnedBy', centralHexKey: '0,-4', color: 'blue'},
    },
  ],

  botScript: {
    '4|supply|': [
      {op: 'autoSupply'},
    ],
    '4|construction|': [
      {op: 'interceptInitiative'},
    ],
    '4|war|movement': [
      // Рейдер ждет, пока игрок прочтет модалку о перехвате инициативы и
      // откроет карточку фракции орков с кнопки завершения хода
      {op: 'waitTutorialStepPassed', stepId: 'faction_card_from_button'},
      {op: 'move', fromHexKey: '3,3', toHexKey: '2,3', path: ['3,3', '2,3']},
    ],
    '5|supply|': [
      {op: 'autoSupply'},
    ],
    '6|supply|': [
      {op: 'autoSupply'},
    ],
    '7|supply|': [
      {op: 'autoSupply'},
    ],
    '8|supply|': [
      {op: 'autoSupply'},
    ],
    '9|supply|': [
      {op: 'autoSupply'},
    ],
    '10|supply|': [
      {op: 'autoSupply'},
    ],
  },
};
